/**
 * Crisp Chat Mobile Dock Handler
 *
 * Adjusts Crisp chat icon position when mobile dock is visible
 * to prevent overlapping at the bottom of the screen.
 *
 * @version 1.0.0
 * @date 2025-10-10
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    crispSelector: '.crisp-client',
    crispBubbleSelector: '.cc-18ov6',
    mobileDockSelector: 'mobile-dock',
    checkInterval: 100,  // Check every 100ms for Crisp to load
    maxAttempts: 50,     // Try for 5 seconds (50 * 100ms)
    transitionDuration: 300, // Match mobile dock transition
    bottomOffset: 20,    // Additional padding from mobile dock
    debugMode: false
  };

  class CrispMobileDockHandler {
    constructor() {
      this.crispElement = null;
      this.crispBubble = null;
      this.mobileDock = null;
      this.attempts = 0;
      this.isInitialized = false;
      this.currentOffset = 0;

      this.init();
    }

    init() {
      this.log('Initializing Crisp Mobile Dock Handler...');
      this.waitForCrisp();
    }

    /**
     * Wait for Crisp chat widget to load in the DOM
     */
    waitForCrisp() {
      const checkCrisp = () => {
        this.crispElement = document.querySelector(CONFIG.crispSelector);
        this.crispBubble = document.querySelector(CONFIG.crispBubbleSelector);

        if (this.crispElement && this.crispBubble) {
          this.log('Crisp elements found!', { crispElement: this.crispElement, crispBubble: this.crispBubble });
          this.setupHandler();
        } else if (this.attempts < CONFIG.maxAttempts) {
          this.attempts++;
          setTimeout(checkCrisp, CONFIG.checkInterval);
          if (this.attempts % 10 === 0) {
            this.log(`Still waiting for Crisp... attempt ${this.attempts}/${CONFIG.maxAttempts}`);
          }
        } else {
          this.log('Crisp elements not found after maximum attempts', {
            hasClient: !!this.crispElement,
            hasBubble: !!this.crispBubble
          });
        }
      };

      checkCrisp();
    }

    /**
     * Set up the mobile dock observer and event listeners
     */
    setupHandler() {
      this.mobileDock = document.querySelector(CONFIG.mobileDockSelector);

      if (!this.mobileDock) {
        this.log('Mobile dock not found - handler not needed');
        return;
      }

      // Add transition styles to Crisp element
      this.addCrispStyles();

      // Watch for mobile dock class changes (is-active)
      this.observeMobileDock();

      // Watch for sticky button visibility and position changes
      this.observeStickyButton();

      // Listen for scroll events (mobile dock appears/disappears on scroll)
      this.setupScrollListener();

      // Initial position check
      this.updateCrispPosition();

      this.isInitialized = true;
      this.log('Crisp Mobile Dock Handler initialized successfully');
    }

    /**
     * Add transition styles to Crisp chat bubble element
     */
    addCrispStyles() {
      if (!this.crispBubble) return;

      // Apply smooth transition for transform on the bubble
      this.crispBubble.style.transition = `transform ${CONFIG.transitionDuration}ms ease`;
      this.crispBubble.style.willChange = 'transform';
      this.log('Added transition styles to Crisp bubble element', {
        element: this.crispBubble,
        className: this.crispBubble.className,
        currentTransform: window.getComputedStyle(this.crispBubble).transform
      });
    }

    /**
     * Observe mobile dock class changes using MutationObserver
     */
    observeMobileDock() {
      if (!this.mobileDock) return;

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            this.updateCrispPosition();
          }
        });
      });

      observer.observe(this.mobileDock, {
        attributes: true,
        attributeFilter: ['class']
      });

      this.log('MutationObserver attached to mobile dock');
    }

    /**
     * Observe sticky button visibility and position changes
     */
    observeStickyButton() {
      // Wait for sticky button to be created
      const waitForStickyButton = () => {
        const stickyButton = document.querySelector('.configurator-sticky-cart-container.right-positioned');

        if (stickyButton) {
          this.log('Sticky button found, attaching observer');

          // Watch for class changes (visible state)
          const classObserver = new MutationObserver(() => {
            this.updateCrispPosition();
          });

          classObserver.observe(stickyButton, {
            attributes: true,
            attributeFilter: ['class']
          });

          // Watch for style changes (bottom position changes)
          const styleObserver = new MutationObserver(() => {
            this.updateCrispPosition();
          });

          styleObserver.observe(stickyButton, {
            attributes: true,
            attributeFilter: ['style']
          });

          this.log('MutationObservers attached to sticky button');
        } else {
          // Retry after a short delay if not found yet
          setTimeout(waitForStickyButton, 100);
        }
      };

      waitForStickyButton();
    }

    /**
     * Set up scroll listener for additional checks
     */
    setupScrollListener() {
      let scrollTimeout;

      window.addEventListener('scroll', () => {
        // Debounce scroll events
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this.updateCrispPosition();
        }, 50);
      }, { passive: true });

      this.log('Scroll listener attached');
    }

    /**
     * Update Crisp chat bubble position to stay 20px above sticky add-to-cart button
     */
    updateCrispPosition() {
      if (!this.crispBubble || !this.mobileDock) {
        this.log('Missing elements:', {
          hasBubble: !!this.crispBubble,
          hasDock: !!this.mobileDock
        });
        return;
      }

      // Find the sticky add-to-cart button
      const stickyButton = document.querySelector('.configurator-sticky-cart-container.right-positioned');

      let newOffset = 0;

      if (stickyButton && stickyButton.classList.contains('visible')) {
        // Sticky button is visible - position Crisp 5px below the top (adjusted up 25px from -30px)
        const stickyButtonRect = stickyButton.getBoundingClientRect();
        const stickyButtonHeight = stickyButtonRect.height;

        // Get the sticky button's current bottom position
        const stickyButtonBottom = parseInt(window.getComputedStyle(stickyButton).bottom) || 0;

        // Calculate total offset: sticky button's bottom position + its height - 5px gap
        newOffset = stickyButtonBottom + stickyButtonHeight - 5;

        this.log(`Sticky button visible - positioning Crisp ${newOffset}px from bottom`, {
          stickyBottom: stickyButtonBottom,
          stickyHeight: stickyButtonHeight,
          gap: -5
        });
      } else {
        // Fallback to mobile dock if sticky button not visible
        const isDockVisible = this.mobileDock.classList.contains('is-active');
        const dockHeight = this.mobileDock.offsetHeight;
        newOffset = isDockVisible && dockHeight > 0 ? dockHeight + CONFIG.bottomOffset : 0;

        this.log(`Using mobile dock offset: ${newOffset}px`, {
          isDockVisible,
          dockHeight
        });
      }

      // Only update if offset actually changed
      if (newOffset !== this.currentOffset) {
        this.currentOffset = newOffset;

        if (newOffset > 0) {
          // Use setProperty with important flag to override Crisp's inline styles
          this.crispBubble.style.setProperty('transform', `translateY(-${newOffset}px)`, 'important');
          this.log(`Moved Crisp bubble up by ${newOffset}px`, {
            element: this.crispBubble,
            transform: this.crispBubble.style.transform,
            computedStyle: window.getComputedStyle(this.crispBubble).transform
          });
        } else {
          // Reset to default position
          this.crispBubble.style.removeProperty('transform');
          this.log('Reset Crisp bubble to default position');
        }
      }
    }

    /**
     * Debug logging
     */
    log(...args) {
      if (CONFIG.debugMode) {
        console.log('[CrispMobileDock]', ...args);
      }
    }

    /**
     * Enable debug mode
     */
    static enableDebug() {
      CONFIG.debugMode = true;
      console.log('[CrispMobileDock] Debug mode enabled');
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.crispMobileDockHandler = new CrispMobileDockHandler();
    });
  } else {
    window.crispMobileDockHandler = new CrispMobileDockHandler();
  }

  // Expose debug method globally
  window.enableCrispDebug = () => CrispMobileDockHandler.enableDebug();

  /**
   * Listen for postMessage from PitchPrint iframe to open Crisp chat
   * Used when user clicks "Click to chat with us" in the help panel
   */
  window.addEventListener('message', function(event) {
    // Security: Check if message is from our domain or PitchPrint
    // Accept messages from any origin for now (PitchPrint iframe is cross-origin)

    if (event.data && event.data.type === 'openCrispChat') {
      console.log('[CrispMobileDock] Received request to open Crisp chat from iframe');

      // Check if Crisp is available
      if (window.$crisp) {
        try {
          window.$crisp.push(['do', 'chat:open']);
          console.log('[CrispMobileDock] Crisp chat opened successfully');
        } catch (error) {
          console.error('[CrispMobileDock] Error opening Crisp chat:', error);
        }
      } else {
        console.warn('[CrispMobileDock] Crisp chat not available yet');
      }
    }
  });

})();
